import React, { FormEvent, useCallback, useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import styled from 'styled-components'
import { Link, useNavigate } from 'react-router-dom'

import useDefer, { Status } from '../../../common/hooks/useDefer'
import Text from '../../../ui/Text'
import { LandingPageLayout, SContentWrapper, SSpacer } from '../../../lib/landing-page-layout'
import requestPasswordReset from '../../../lib/requestPasswordReset'
import { currentCompany, Company } from '../../../lib/current-company'
import currentDomain, { SiteDomain } from '../../../lib/currentDomain'
import { validateEmailFormat } from '../../../lib/validate-email-format'
import { InputGroup, SInput } from '../../../lib/input-group'
import { FormHeader } from '../../../lib/form-header'
import { SubmitButton } from '../../../lib/submit-button'
import { theme } from '../../../lib/theme'
import useText from '../../hooks/useText'

const SFormWrapper = styled(SContentWrapper)`
    max-width: 510px;
    padding: 0 10px;
`

const SLink = styled.a`
    &,
    &:hover,
    &:active,
    &:visited,
    &:focus {
        text-decoration: none;
        color: ${({ theme }) => theme.font.midDarkColor};
        font-weight: 400;
        text-align: center;
        width: 100%;
        display: inline-block;
        padding-top: 30px;
    }
`

const SInstruction = styled.div`
    line-height: 1.5em;
    font-weight: 400;
    padding-bottom: 37px;
    text-align: center;
`

const SHeaderWrapper = styled.div`
    padding-bottom: 45px;
`

export type ResetPasswordRequestPageProps = {
    className?: ClassName
    style?: React.CSSProperties
}

export function ResetPasswordRequestPage({ className, style }: ResetPasswordRequestPageProps): React.ReactElement {
    const text = useText()
    const [email, setEmail] = useState('')
    const [errorMessage, setErrorMessage] = useState('')
    const navigate = useNavigate()

    const { execute, status } = useDefer((email: string) =>
        requestPasswordReset(email)
            .then(() => {
                navigate('/reset-password-request-success', { replace: true })
            })
            .catch((error: { messageCode?: string }) => {
                if (error?.messageCode === 'msgEmailDoesntExist') {
                    setErrorMessage(text('msgEmailDoesntExist', { company: currentCompany }))
                    return
                }
                navigate('/reset-password-request-error', { replace: true })
            })
    )

    const handleSubmit = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault()

            if (!validateEmailFormat(email)) {
                setErrorMessage(text('msgEmailFormatIncorrect'))
                return
            }

            execute(email)
        },
        [email]
    )
    return (
        <LandingPageLayout
            className={cn(className)}
            style={style}
            background={
                currentCompany === Company.FORTUM && currentDomain === SiteDomain.ADMIN
                    ? theme.greenBackground
                    : theme.whiteBackground
            }
            hideLanguageSelect
        >
            <SFormWrapper>
                <SHeaderWrapper>
                    <FormHeader data-testid="header" />
                </SHeaderWrapper>
                <SInstruction data-testid="description">
                    {currentCompany === Company.FORTUM && currentDomain === SiteDomain.ADMIN ? (
                        <Text code="msgFortumResetPasswordInstruction" />
                    ) : (
                        <Text code="msgChargeAmpsResetPasswordInstruction" />
                    )}
                </SInstruction>
                <form onSubmit={handleSubmit} data-testid="form" noValidate>
                    <SSpacer>
                        <InputGroup
                            errorTestId="errorMsg"
                            errorMsg={errorMessage}
                            inverted={false}
                            label={email && text('lblEmailField')}
                        >
                            <SInput
                                placeholder={text('lblEmailField')}
                                onInput={(event) => {
                                    const value = (event.target as HTMLInputElement).value
                                    setErrorMessage('')
                                    setEmail(value)
                                }}
                                value={email}
                                data-testid="emailInput"
                            />
                        </InputGroup>
                    </SSpacer>
                    <SubmitButton disabled={status === Status.PENDING}>
                        <Text code="btnResetPassword" />
                    </SubmitButton>
                </form>
                <SLink as={Link} to="/login" data-testid="returnBtn">
                    <Text code="btnReturnToSignIn" />
                </SLink>
            </SFormWrapper>
        </LandingPageLayout>
    )
}
